/**
 * @zendir/ui - UnifiedTimeline Component
 *
 * Enterprise-grade unified timeline with seamless view switching between
 * Gantt chart view and Event list view.
 *
 * Design Philosophy (Apple/Google inspired):
 * - Single component, multiple views
 * - Smooth transitions between views
 * - Consistent data model
 * - Flexible for teams and single user
 *
 * @example
 * ```tsx
 * <UnifiedTimeline
 *   title="Mission Timeline"
 *   events={events}
 *   tracks={tracks}
 *   defaultView="list"
 * />
 * ```
 */

import React, {
  memo,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useTheme } from "../theme";
import type { ThemeTokens } from "../theme";
import { classNames, safeAccentText } from "../utils";
import type { CategoryDef } from "../types";
import { useCategoryPalette } from "../context/CategoryContext";
import { Icon } from "../core/display/Icon";
import { Badge } from "../core/display/Badge";
import { Tooltip } from "../core/overlays/Tooltip";
import { Button } from "../core/inputs/Button";
import { useBreakpoint } from "../core/layout/useBreakpoint";
// ============================================================================
// Types
// ============================================================================

// ============================================================================
// Z-Index scale — prevents conflicts with global modals, toasts, nav overlays
// ============================================================================
const Z = {
  /** Base layer for track events / scatter dots */
  trackEvent: 1,
  /** Hovered track event raised above siblings */
  trackEventHovered: 20,
  /** Sticky column elements (playhead, track labels) */
  sticky: 15,
  /** Floating day-marker lines above track content */
  dayMarker: 5,
  /** Current-time playhead line — above sticky headers and events */
  playhead: 25,
  /** Loading overlay covering chart content */
  loadingOverlay: 100,
  /** Floating tooltips — above all chart chrome */
  tooltip: 1000,
} as const;

/** Filter key for events with no team accent (use with `TimelineFilter.teams`). */
export const TIMELINE_FILTER_TEAM_NONE = "__zendir_team_none__";
/** Filter key for legacy accent color only (no structured `team.id`). */
export const TIMELINE_FILTER_TEAM_LEGACY = "__zendir_team_legacy__";

export type TimelineViewMode = "list" | "chart" | "scatter";

/**
 * Team / category identity on the timeline.
 *
 * Extends the SDK-level `CategoryDef` with `label` kept optional for
 * backward compatibility — existing code that passes `{ id, color }` without
 * a label continues to compile and run unchanged.
 *
 * Because it extends `CategoryDef`, a `TimelineTeam` value can be used
 * anywhere a `CategoryDef` is expected (e.g. `CategoryPalette` constructor).
 */
export interface TimelineTeam extends Omit<CategoryDef, "label"> {
  /** Human-readable name (tooltips, search, filter pills). Optional for backward compat. */
  label?: string;
}

export interface TimelineEvent {
  /** Unique identifier */
  id: string;
  /** Event title */
  title: string;
  /** Start time */
  start: Date;
  /** End time (optional for point events) */
  end?: Date;
  /** Track/category this event belongs to */
  track?: string;
  /** Status for coloring */
  status?: "off" | "standby" | "normal" | "caution" | "serious" | "critical";
  /** Badge text */
  badge?: string;
  /** Badge variant - uses AstroUXDS status terms */
  badgeVariant?:
    | "default"
    | "primary"
    | "normal"
    | "caution"
    | "serious"
    | "critical";
  /** Subtitle/description */
  subtitle?: string;
  /** Additional timestamps to display in list view */
  timestamps?: {
    label: string;
    value: string;
    status?: "normal" | "standby" | "caution" | "serious" | "critical" | "off";
  }[];
  /** Arguments/metadata */
  arguments?: Record<string, string | number | boolean>;
  /** Custom metadata */
  metadata?: Record<string, unknown>;
  /**
   * Team assignment (preferred): id + accent color (+ optional label).
   * List: thin top stripe. Chart/Gantt: thin top stripe; status via Astro shape + hover.
   * Scatter: outer ring. Does NOT override status fill.
   */
  team?: TimelineTeam;
  /**
   * @deprecated Prefer `team: { id, color, label? }`. If set without `team`, still used as the accent color (legacy).
   */
  color?: string;
}

/** Resolved team accent for rendering (from `event.team` or legacy `event.color`). */
export function getTimelineTeamAccent(
  event: TimelineEvent,
): { color: string; id?: string | number; label?: string } | null {
  const t = event.team;
  if (t?.color) {
    return { color: t.color, id: t.id, label: t.label };
  }
  if (event.color) {
    return { color: event.color };
  }
  return null;
}

/** Label for team row in tooltips (id / label / arguments.Team / badge). */
export function getTimelineTeamDisplayLabel(
  event: TimelineEvent,
  accent: NonNullable<ReturnType<typeof getTimelineTeamAccent>>,
): string {
  if (accent.label) return accent.label;
  if (accent.id !== undefined && accent.id !== "") return String(accent.id);
  const args = event.arguments as Record<string, unknown> | undefined;
  const argsTeam = args?.Team;
  if (argsTeam !== undefined && argsTeam !== "") return String(argsTeam);
  return event.badge || "Team";
}

/** Stable key for team filter pills (`TimelineFilter.teams`). */
export function getTimelineTeamFilterKey(event: TimelineEvent): string {
  const accent = getTimelineTeamAccent(event);
  if (!accent) return TIMELINE_FILTER_TEAM_NONE;
  if (accent.id === undefined || accent.id === "")
    return TIMELINE_FILTER_TEAM_LEGACY;
  return String(accent.id);
}

export interface TimelineTrackDef {
  /** Track identifier */
  id: string;
  /** Display label */
  label: string;
  /** Track height in pixels */
  height?: number;
}

export type TimeFormat = "relative" | "absolute" | "utc";

export interface TimelineFilter {
  /** Filter by track IDs (empty / omitted = all tracks) */
  tracks?: string[];
  /** Filter by display status (multi-select; empty / omitted = all) */
  status?: TimelineEvent["status"][];
  /** Case-insensitive match on title, subtitle, badge, track, argument values */
  search?: string;
  /** Duration shape: point (no end or zero length), range (has end after start), all */
  eventShape?: "all" | "point" | "range";
  /**
   * Filter by team keys from `getTimelineTeamFilterKey` (e.g. `"42"`, `"alpha"`).
   * Include `TIMELINE_FILTER_TEAM_NONE` / `TIMELINE_FILTER_TEAM_LEGACY` when those buckets exist in data.
   */
  teams?: string[];
  /**
   * @deprecated Use `teams` with explicit keys. When true (and `teams` is empty), only events with a team accent match.
   */
  teamColoredOnly?: boolean;
}

const ALL_TIMELINE_STATUSES: TimelineEvent["status"][] = [
  "off",
  "standby",
  "normal",
  "caution",
  "serious",
  "critical",
];

/** Display status used for filtering and markers (badgeVariant overrides raw status when set). */
export function getTimelineEventDisplayStatus(
  event: TimelineEvent,
): TimelineEvent["status"] {
  if (
    event.badgeVariant &&
    event.badgeVariant !== "default" &&
    event.badgeVariant !== "primary"
  ) {
    return event.badgeVariant as TimelineEvent["status"];
  }
  return event.status ?? "normal";
}

export function isTimelineFilterActive(filter: TimelineFilter): boolean {
  return Boolean(
    (filter.search && filter.search.trim()) ||
    (filter.tracks && filter.tracks.length > 0) ||
    (filter.status && filter.status.length > 0) ||
    (filter.eventShape && filter.eventShape !== "all") ||
    (filter.teams && filter.teams.length > 0) ||
    filter.teamColoredOnly,
  );
}

function normalizeTimelineFilter(f: TimelineFilter): TimelineFilter {
  const o: TimelineFilter = {};
  const s = f.search?.trim();
  if (s) o.search = s;
  if (f.tracks?.length) o.tracks = [...f.tracks];
  if (f.status?.length) o.status = [...f.status];
  if (f.eventShape && f.eventShape !== "all") o.eventShape = f.eventShape;
  if (f.teams?.length) o.teams = [...f.teams];
  if (f.teamColoredOnly) o.teamColoredOnly = true;
  return o;
}

export function matchesTimelineFilter(
  event: TimelineEvent,
  filter: TimelineFilter,
): boolean {
  if (!isTimelineFilterActive(filter)) return true;

  if (filter.tracks?.length) {
    const tid = event.track ?? "default";
    if (!filter.tracks.includes(tid)) return false;
  }

  if (filter.status?.length) {
    const ds = getTimelineEventDisplayStatus(event);
    if (!filter.status.includes(ds)) return false;
  }

  const q = filter.search?.trim().toLowerCase();
  if (q) {
    const argStr = event.arguments
      ? Object.values(event.arguments)
          .map((v) => String(v))
          .join(" ")
      : "";
    const accent = getTimelineTeamAccent(event);
    const teamHay = accent
      ? [accent.label, accent.id !== undefined ? String(accent.id) : ""]
          .filter(Boolean)
          .join(" ")
      : "";
    const hay = [
      event.title,
      event.subtitle,
      event.badge,
      event.track,
      argStr,
      teamHay,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }

  if (filter.eventShape === "point") {
    const endT = event.end?.getTime();
    const st = event.start.getTime();
    if (endT != null && endT > st) return false;
  }
  if (filter.eventShape === "range") {
    const endT = event.end?.getTime();
    const st = event.start.getTime();
    if (endT == null || endT <= st) return false;
  }

  if (filter.teams?.length) {
    const teamKey = getTimelineTeamFilterKey(event);
    if (!filter.teams.includes(teamKey)) return false;
  }

  if (
    filter.teamColoredOnly &&
    !(filter.teams && filter.teams.length > 0) &&
    !getTimelineTeamAccent(event)
  ) {
    return false;
  }

  return true;
}

function countTimelineFilterChips(filter: TimelineFilter): number {
  let n = 0;
  if (filter.search?.trim()) n++;
  if (filter.tracks?.length) n++;
  if (filter.status?.length) n++;
  if (filter.eventShape && filter.eventShape !== "all") n++;
  if (filter.teams && filter.teams.length > 0) n++;
  if (filter.teamColoredOnly && !(filter.teams && filter.teams.length > 0)) n++;
  return n;
}

export interface UnifiedTimelineProps {
  /** Timeline title */
  title?: string;
  /** Events to display */
  events: TimelineEvent[];
  /** Track definitions for chart view */
  tracks?: TimelineTrackDef[];
  /** Start time for chart view (auto-calculated if not provided) */
  start?: Date;
  /** End time for chart view (auto-calculated if not provided) */
  end?: Date;
  /** Default view mode */
  defaultView?: TimelineViewMode;
  /** Controlled view mode */
  viewMode?: TimelineViewMode;
  /** View mode change handler */
  onViewModeChange?: (mode: TimelineViewMode) => void;
  /** Event click handler */
  onEventClick?: (event: TimelineEvent) => void;
  /** Show view toggle */
  showViewToggle?: boolean;
  /** Max height for scrollable container */
  maxHeight?: string | number;
  /** Track height for chart view */
  trackHeight?: number;
  /** Custom className */
  className?: string;
  /** Time format for axis labels */
  timeFormat?: TimeFormat;
  /** Enable zoom controls */
  zoomable?: boolean;
  /** Initial zoom level (1 = 100%) */
  initialZoom?: number;
  /** Show filter & search controls in header (default true) */
  showFilters?: boolean;
  /** Start with the filter panel expanded (default false) */
  filtersDefaultExpanded?: boolean;
  /** When any filter is active, hide chart/scatter track rows that have no matching events */
  hideEmptyTracksWhenFiltered?: boolean;
  /** Controlled filter state */
  filter?: TimelineFilter;
  /** Filter change handler (receives normalized filter object) */
  onFilterChange?: (filter: TimelineFilter) => void;
  /** Show event count badge */
  showCount?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Reference time for relative format (defaults to start of range) */
  referenceTime?: Date;

  // === Playhead Options (Astro UX) ===
  /** Show playhead indicator at current time */
  showPlayhead?: boolean;
  /** Custom playhead time (for simulation mode) */
  playheadTime?: Date;
  /** Callback when playhead time changes (for interactive playhead) */
  onPlayheadChange?: (time: Date) => void;

  // === Day Markers (Astro UX) ===
  /** Show day markers at midnight */
  showDayMarkers?: boolean;

  // === Customization ===
  /**
   * Override the label used for the "Teams" secondary-category filter section.
   * Useful when the `team` field on events represents squads, categories, groups, etc.
   * Defaults to `"Teams"`. The value is also used for ARIA labels and tooltip text.
   * @example teamLabel="Squads"
   * @example teamLabel="Categories"
   */
  teamLabel?: string;
}

// ============================================================================
// Event List Item (for list view)
// ============================================================================

/** Astro UX status shapes per official spec: filled circle (normal), ring (standby), small circle (off), square (caution), diamond (serious), triangle (critical) */
function TimelineStatusMarker({
  status,
  fillColor,
  size = 12,
}: {
  status?: TimelineEvent["status"];
  fillColor: string;
  size?: number;
}): React.ReactElement {
  const effectiveStatus = status ?? "normal";
  const glow = `${fillColor}60`;

  switch (effectiveStatus) {
    case "off":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${glow})` }}
        >
          <circle cx="6" cy="6" r="3" fill={fillColor} />
        </svg>
      );
    case "standby":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${glow})` }}
        >
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={fillColor}
            strokeWidth="2"
          />
        </svg>
      );
    case "normal":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${glow})` }}
        >
          <circle cx="6" cy="6" r="5" fill={fillColor} />
        </svg>
      );
    case "caution":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${glow})` }}
        >
          <rect x="1" y="1" width="10" height="10" fill={fillColor} />
        </svg>
      );
    case "serious":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${glow})` }}
        >
          <polygon points="6,1 11,6 6,11 1,6" fill={fillColor} />
        </svg>
      );
    case "critical":
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${glow})` }}
        >
          <polygon points="6,11 1,2 11,2" fill={fillColor} />
        </svg>
      );
    default: // normal
      return (
        <svg
          viewBox="0 0 12 12"
          width={size}
          height={size}
          style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${glow})` }}
        >
          <circle cx="6" cy="6" r="5" fill={fillColor} />
        </svg>
      );
  }
}

interface EventListItemProps {
  event: TimelineEvent;
  isLast: boolean;
  onEventClick?: (event: TimelineEvent) => void;
}

const EventListItem = memo(function EventListItem({
  event,
  isLast,
  onEventClick,
}: EventListItemProps) {
  const { tokens } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const hasArguments =
    event.arguments && Object.keys(event.arguments).length > 0;
  const argumentCount = hasArguments ? Object.keys(event.arguments!).length : 0;

  // Generate timestamps from start/end if not provided
  const timestamps = event.timestamps || [
    {
      label: "Time",
      value:
        event.start.toISOString().replace("T", " ").substring(0, 19) + " UTC",
      status: event.status as any,
    },
  ];

  // Single source of truth: badgeVariant overrides status so badge and marker always match
  const displayStatus: TimelineEvent["status"] =
    event.badgeVariant &&
    event.badgeVariant !== "default" &&
    event.badgeVariant !== "primary"
      ? event.badgeVariant
      : (event.status ?? "normal");
  const markerColor = tokens.colors.status[displayStatus];
  const teamAccent = getTimelineTeamAccent(event);

  return (
    <div style={{ display: "flex", gap: "16px" }}>
      {/* Timeline indicator - Astro UX status shape (circle/square/diamond/triangle) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "20px",
          flexShrink: 0,
        }}
      >
        <div style={{ marginTop: "8px" }}>
          <TimelineStatusMarker
            status={displayStatus}
            fillColor={markerColor}
            size={12}
          />
        </div>
        {!isLast && (
          <div
            style={{
              width: "2px",
              flex: 1,
              backgroundColor: `${tokens.colors.accent.primary}30`,
              marginTop: "4px",
            }}
          />
        )}
      </div>

      {/* Event card */}
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onEventClick?.(event)}
        style={{
          position: "relative",
          flex: 1,
          marginBottom: "16px",
          padding: teamAccent ? "19px 20px 16px 20px" : "16px 20px",
          backgroundColor: tokens.colors.background.surface,
          border: `1px solid ${hovered ? tokens.colors.accent.primary : tokens.colors.border.muted}`,
          borderRadius: tokens.borderRadius.lg,
          cursor: onEventClick ? "pointer" : "default",
          transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          boxShadow: hovered
            ? `0 4px 20px ${tokens.colors.accent.primary}15, 0 0 0 1px ${tokens.colors.accent.primary}20`
            : "none",
        }}
      >
        {/* Team accent top stripe (id + color via `team` or legacy `color`) */}
        {teamAccent && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: teamAccent.color,
            }}
          />
        )}

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: timestamps.length ? "12px" : 0,
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 500, // AstroUXDS medium (was 600)
              color: tokens.colors.text.primary,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            {event.title}
          </h4>
          {event.badge && (
            <Badge
              variant={
                displayStatus === "caution" || displayStatus === "serious"
                  ? "filled"
                  : "outline"
              }
              size="small"
              status={
                displayStatus === "critical"
                  ? "critical"
                  : displayStatus === "serious"
                    ? "serious"
                    : displayStatus === "caution"
                      ? "caution"
                      : displayStatus === "normal"
                        ? "normal"
                        : undefined
              }
            >
              {event.badge}
            </Badge>
          )}
        </div>

        {/* Subtitle */}
        {event.subtitle && (
          <p
            style={{
              margin: "0 0 12px 0",
              fontSize: "0.8125rem",
              color: tokens.colors.text.secondary,
            }}
          >
            {event.subtitle}
          </p>
        )}

        {/* Timestamps */}
        {timestamps.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {timestamps.map((ts, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "0.8125rem",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: tokens.colors.text.secondary,
                  }}
                >
                  <TimelineStatusMarker
                    status={ts.status ?? "normal"}
                    fillColor={
                      ts.status
                        ? tokens.colors.status[
                            ts.status as keyof typeof tokens.colors.status
                          ]
                        : tokens.colors.accent.primary
                    }
                    size={6}
                  />
                  {ts.label}
                </span>
                <span
                  style={{
                    fontFamily: tokens.typography.fontFamily.mono,
                    color: tokens.colors.text.primary,
                    fontWeight: 500,
                  }}
                >
                  {ts.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Arguments toggle */}
        {hasArguments && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginTop: "12px",
                padding: 0,
                background: "none",
                border: "none",
                color: tokens.colors.text.secondary,
                fontSize: "0.75rem",
                cursor: "pointer",
                transition: "color 150ms ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = tokens.colors.accent.primary)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = tokens.colors.text.secondary)
              }
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="currentColor"
                style={{
                  transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 150ms ease",
                }}
              >
                <path d="M8 5v14l11-7z" />
              </svg>
              {expanded ? "Hide" : "Show"} Arguments ({argumentCount})
            </button>

            {expanded && (
              <div
                style={{
                  marginTop: "12px",
                  borderTop: `1px solid ${tokens.colors.border.muted}`,
                  paddingTop: "12px",
                  animation: "zendir-expand 200ms ease-out",
                }}
              >
                <style>
                  {`@keyframes zendir-expand { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }`}
                </style>
                {Object.entries(event.arguments!).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      marginBottom: "4px",
                      backgroundColor: tokens.colors.background.elevated,
                      borderRadius: tokens.borderRadius.md,
                      fontSize: "0.8125rem",
                    }}
                  >
                    <span style={{ color: tokens.colors.text.secondary }}>
                      {key}
                    </span>
                    <span
                      style={{
                        fontFamily: tokens.typography.fontFamily.mono,
                        color:
                          typeof value === "boolean"
                            ? value
                              ? tokens.colors.status.normal
                              : tokens.colors.text.tertiary
                            : tokens.colors.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      {String(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

// ============================================================================
// Chart View (Gantt-style) - Matches original Timeline design
// ============================================================================

interface ChartViewProps {
  events: TimelineEvent[];
  tracks: TimelineTrackDef[];
  start: Date;
  end: Date;
  trackHeight: number;
  onEventClick?: (event: TimelineEvent) => void;
  zoom: number;
  showPlayhead?: boolean;
  playheadTime?: Date;
  onPlayheadChange?: (time: Date) => void;
  showDayMarkers?: boolean;
  timeFormat?: TimeFormat;
}

const ChartView = memo(function ChartView({
  events,
  tracks,
  start,
  end,
  trackHeight,
  onEventClick,
  zoom,
  showPlayhead = true,
  playheadTime,
  onPlayheadChange: _onPlayheadChange,
  showDayMarkers = true,
  timeFormat = "absolute",
}: ChartViewProps) {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  /* scrollX tracks the horizontal scroll offset so events outside the visible area can be skipped.
     This provides cheap bounding-box culling without a virtual-DOM library. */
  const [scrollX, setScrollX] = useState(0);

  const totalDuration = end.getTime() - start.getTime();
  const trackLabelWidth = 130;

  // Generate time labels (adjust based on zoom)
  const timeLabels = useMemo(() => {
    const labels: { time: Date; percent: number }[] = [];
    // More labels when zoomed in, fewer when zoomed out
    const baseLabelCount = 6;
    const labelCount = Math.round(baseLabelCount * zoom);

    for (let i = 0; i <= labelCount; i++) {
      const percent = (i / labelCount) * 100;
      const time = new Date(start.getTime() + (totalDuration * percent) / 100);
      labels.push({ time, percent });
    }
    return labels;
  }, [start, totalDuration, zoom]);

  // Group events by track with overlap detection
  const eventsByTrack = useMemo(() => {
    const grouped: Record<string, TimelineEvent[]> = {};
    tracks.forEach((track) => {
      grouped[track.id] = events.filter((e) => e.track === track.id);
    });
    // Add unassigned events to first track
    const unassigned = events.filter((e) => !e.track);
    if (unassigned.length > 0 && tracks.length > 0) {
      grouped[tracks[0].id] = [...(grouped[tracks[0].id] || []), ...unassigned];
    }
    return grouped;
  }, [events, tracks]);

  // Detect overlapping events and calculate stacking
  const eventOverlaps = useMemo(() => {
    const overlaps: Record<
      string,
      { stackIndex: number; overlapCount: number; overlappingIds: string[] }
    > = {};

    Object.entries(eventsByTrack).forEach(([_trackId, trackEvents]) => {
      // Sort events by start time
      const sorted = [...trackEvents].sort(
        (a, b) => a.start.getTime() - b.start.getTime(),
      );

      sorted.forEach((event) => {
        const eventStart = event.start.getTime();
        const eventEnd = (event.end || new Date(eventStart + 300000)).getTime();

        // Find all overlapping events
        const overlapping = sorted.filter((other) => {
          if (other.id === event.id) return false;
          const otherStart = other.start.getTime();
          const otherEnd = (
            other.end || new Date(otherStart + 300000)
          ).getTime();
          return eventStart < otherEnd && eventEnd > otherStart;
        });

        // Calculate stack index (position in vertical stack)
        let stackIndex = 0;
        if (overlapping.length > 0) {
          const usedIndices = overlapping
            .filter((o) => overlaps[o.id] !== undefined)
            .map((o) => overlaps[o.id].stackIndex);
          while (usedIndices.includes(stackIndex)) {
            stackIndex++;
          }
        }

        overlaps[event.id] = {
          stackIndex,
          overlapCount: overlapping.length,
          overlappingIds: overlapping.map((o) => o.id),
        };
      });
    });

    return overlaps;
  }, [eventsByTrack]);

  // Generate day markers at midnight
  const dayMarkers = useMemo(() => {
    const markers: { time: Date; percent: number }[] = [];
    const current = new Date(start);

    // Move to midnight of the first day
    current.setUTCHours(0, 0, 0, 0);
    if (current.getTime() < start.getTime()) {
      current.setUTCDate(current.getUTCDate() + 1);
    }

    while (current.getTime() <= end.getTime()) {
      const percent =
        ((current.getTime() - start.getTime()) / totalDuration) * 100;
      if (percent >= 0 && percent <= 100) {
        markers.push({ time: new Date(current), percent });
      }
      current.setUTCDate(current.getUTCDate() + 1);
    }

    return markers;
  }, [start, end, totalDuration]);

  // Format helpers — respect timeFormat prop so axis labels match the app's clock
  const formatTime = (date: Date, showSeconds = false) => {
    if (timeFormat === "utc") {
      const h = String(date.getUTCHours()).padStart(2, "0");
      const m = String(date.getUTCMinutes()).padStart(2, "0");
      if (showSeconds) {
        const s = String(date.getUTCSeconds()).padStart(2, "0");
        return `${h}:${m}:${s} UTC`;
      }
      return `${h}:${m} UTC`;
    }
    const options: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      ...(showSeconds && { second: "2-digit" }),
    };
    return date.toLocaleTimeString([], options);
  };

  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const formatUTC = (date: Date) => {
    return date.toISOString().replace("T", " ").substring(0, 19) + " UTC";
  };

  const getEventPosition = (event: TimelineEvent) => {
    const eventStart = event.start.getTime();
    const eventEnd = (event.end || new Date(eventStart + 300000)).getTime(); // 5min default
    const leftPercent = ((eventStart - start.getTime()) / totalDuration) * 100;
    const widthPercent = ((eventEnd - eventStart) / totalDuration) * 100;
    return {
      left: `${Math.max(0, Math.min(100, leftPercent))}%`,
      width: `${Math.max(2, Math.min(100 - leftPercent, widthPercent))}%`,
    };
  };

  const getStatusColor = (status?: string) => {
    if (!status) return tokens.colors.accent.primary;
    return (
      tokens.colors.status[status as keyof typeof tokens.colors.status] ||
      tokens.colors.accent.primary
    );
  };

  /**
   * Returns false for events whose rendered position is entirely outside the current horizontal
   * scroll viewport, so we can skip them without touching the DOM. This is cheap per-frame
   * bounding-box culling; the scroll container width is read once from the ref at render time.
   */
  const isEventVisible = useCallback(
    (event: TimelineEvent): boolean => {
      const container = scrollContainerRef.current;
      if (!container) return true; // fallback: always render during SSR / first paint
      const viewportWidth = container.clientWidth - trackLabelWidth;
      if (viewportWidth <= 0) return true;
      const totalWidth = container.scrollWidth - trackLabelWidth;
      const eventStartMs = event.start.getTime();
      const eventEndMs = (
        event.end ?? new Date(eventStartMs + 300_000)
      ).getTime();
      const leftPx =
        ((eventStartMs - start.getTime()) / totalDuration) * totalWidth;
      const rightPx =
        ((eventEndMs - start.getTime()) / totalDuration) * totalWidth;
      /* Add 50px overscan so events sliding into view don't pop in */
      const overscan = 50;
      return (
        rightPx >= scrollX - overscan &&
        leftPx <= scrollX + viewportWidth + overscan
      );
    },
    [start, totalDuration, scrollX, trackLabelWidth],
  );

  const getStatusLabel = (status?: string) => {
    const labels: Record<string, string> = {
      normal: "Normal",
      standby: "Standby",
      caution: "Caution",
      serious: "Serious",
      critical: "Critical",
      off: "Off",
    };
    return labels[status || ""] || "Unknown";
  };

  // Current time playhead (use provided time or current time)
  const currentPlayheadTime = playheadTime || new Date();
  const now = currentPlayheadTime.getTime();
  const playheadPercent =
    showPlayhead && now >= start.getTime() && now <= end.getTime()
      ? ((now - start.getTime()) / totalDuration) * 100
      : null;

  // Auto-follow playhead: keep the playhead marker visible by scrolling the
  // chart view horizontally when the playhead nears the edge of the viewport.
  const lastAutoScrollRef = useRef(0);
  useEffect(() => {
    if (playheadPercent === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const contentWidth = container.scrollWidth;
    const viewportWidth = container.clientWidth;
    if (contentWidth <= viewportWidth) return;
    // Position of the playhead in px (within the scrollable content area past trackLabelWidth)
    const timelineContentWidth = contentWidth - trackLabelWidth;
    const playheadPx =
      trackLabelWidth + (playheadPercent / 100) * timelineContentWidth;
    const scrollLeft = container.scrollLeft;
    const visibleLeft = scrollLeft + trackLabelWidth;
    const visibleRight = scrollLeft + viewportWidth;
    // Scroll when the playhead is beyond 85% of the visible region or before 15%
    const margin = viewportWidth * 0.15;
    if (
      playheadPx > visibleRight - margin ||
      playheadPx < visibleLeft + margin
    ) {
      // Throttle auto-scroll to avoid fighting user scrolling
      const nowMs = Date.now();
      if (nowMs - lastAutoScrollRef.current < 300) return;
      lastAutoScrollRef.current = nowMs;
      const targetScroll = playheadPx - viewportWidth * 0.5;
      container.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: "smooth",
      });
    }
  }, [playheadPercent, trackLabelWidth]);

  // Get hovered event details
  const hoveredEventData = hoveredEvent
    ? events.find((e) => e.id === hoveredEvent)
    : null;

  return (
    <div style={{ position: "relative" }}>
      {/* Scrollable chart area with sticky track labels */}
      <div
        ref={scrollContainerRef}
        className="zendir-timeline-scroll"
        onScroll={(e) =>
          setScrollX((e.currentTarget as HTMLDivElement).scrollLeft)
        }
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            minWidth: "100%",
            position: "relative",
          }}
        >
          {/* Time axis */}
          <div
            style={{
              display: "flex",
              height: 32,
              borderBottom: `1px solid ${tokens.colors.accent.primary}20`,
              position: "sticky",
              top: 0,
              zIndex: Z.trackEventHovered,
              backgroundColor: isTransparentTheme
                ? "transparent"
                : tokens.colors.background.surface,
              ...(isTransparentTheme && {
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)" as const,
              }),
            }}
          >
            {/* Sticky header corner */}
            <div
              style={{
                width: trackLabelWidth,
                flexShrink: 0,
                position: "sticky",
                left: 0,
                zIndex: Z.trackEventHovered + 5,
                backgroundColor: isTransparentTheme
                  ? "transparent"
                  : tokens.colors.background.surface,
                ...(isTransparentTheme && {
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)" as const,
                }),
                borderRight: `1px solid ${tokens.colors.border.muted}30`,
                display: "flex",
                alignItems: "center",
                paddingLeft: 12,
                fontSize: "0.625rem",
                fontWeight: 500, // AstroUXDS medium (was 600)
                color: tokens.colors.text.tertiary,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Tracks
            </div>

            {/* Time labels container */}
            <div style={{ flex: 1, position: "relative" }}>
              {/* Day markers */}
              {showDayMarkers &&
                dayMarkers.map((marker, index) => (
                  <div
                    key={`day-${index}`}
                    style={{
                      position: "absolute",
                      left: `${marker.percent}%`,
                      top: 0,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      zIndex: Z.dayMarker,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.5rem",
                        color: tokens.colors.status.caution,
                        fontFamily: tokens.typography.fontFamily.mono,
                        whiteSpace: "nowrap",
                        backgroundColor: `${tokens.colors.status.caution}20`,
                        padding: "1px 4px",
                        borderRadius: 2,
                        marginTop: 2,
                      }}
                    >
                      {marker.time.toLocaleDateString([], {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                ))}

              {/* Time labels */}
              {timeLabels.map((label, index) => (
                <span
                  key={index}
                  style={{
                    position: "absolute",
                    left: `${label.percent}%`,
                    transform: "translateX(-50%)",
                    top: 8,
                    fontSize: "0.6875rem",
                    color: tokens.colors.text.tertiary,
                    fontFamily: tokens.typography.fontFamily.mono,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatTime(label.time)}
                </span>
              ))}
            </div>
          </div>

          {/* Tracks */}
          {tracks.map((track) => (
            <div
              key={track.id}
              aria-label={track.label}
              style={{
                display: "flex",
                height: track.height || trackHeight,
                borderBottom: `1px solid ${tokens.colors.accent.primary}10`,
              }}
            >
              {/* Track label - STICKY on horizontal scroll */}
              <div
                style={{
                  width: trackLabelWidth,
                  flexShrink: 0,
                  position: "sticky",
                  left: 0,
                  zIndex: Z.sticky,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 12,
                  fontSize: "0.6875rem",
                  fontWeight: 500, // AstroUXDS medium (was 600)
                  color: tokens.colors.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  backgroundColor: isTransparentTheme
                    ? "transparent"
                    : tokens.colors.background.surface,
                  ...(isTransparentTheme && {
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)" as const,
                  }),
                  borderRight: `1px solid ${tokens.colors.border.muted}30`,
                  boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
                }}
              >
                {track.label}
              </div>

              {/* Track events container - separate from label */}
              <div
                style={{
                  flex: 1,
                  position: "relative",
                  height: "100%",
                }}
              >
                {/* Day marker vertical lines */}
                {showDayMarkers &&
                  dayMarkers.map((marker, index) => (
                    <div
                      key={`day-line-${index}`}
                      style={{
                        position: "absolute",
                        left: `${marker.percent}%`,
                        top: 0,
                        bottom: 0,
                        width: 1,
                        backgroundColor: `${tokens.colors.status.caution}25`,
                        zIndex: Z.trackEvent,
                        pointerEvents: "none",
                      }}
                    />
                  ))}

                {(eventsByTrack[track.id] || [])
                  .filter(isEventVisible)
                  .map((event) => {
                    const { left, width } = getEventPosition(event);
                    const displayStatus: TimelineEvent["status"] =
                      event.badgeVariant &&
                      event.badgeVariant !== "default" &&
                      event.badgeVariant !== "primary"
                        ? event.badgeVariant
                        : (event.status ?? "normal");
                    const color = getStatusColor(displayStatus);
                    const isHovered = hoveredEvent === event.id;
                    const overlap = eventOverlaps[event.id];
                    const hasOverlap = overlap && overlap.overlapCount > 0;
                    const teamAccent = getTimelineTeamAccent(event);

                    // Stack events vertically when they overlap
                    const cardHeight = hasOverlap ? 30 : 38;
                    const stackOffset = overlap ? overlap.stackIndex * 30 : 0;
                    const baseTop =
                      ((track.height || trackHeight) - cardHeight) / 2;

                    return (
                      <button
                        key={event.id}
                        type="button"
                        aria-label={`${event.title}${event.status ? `, status: ${event.status}` : ""}`}
                        onClick={() => onEventClick?.(event)}
                        onMouseEnter={(e) => {
                          setHoveredEvent(event.id);
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPos({
                            x: rect.left + rect.width / 2,
                            y: rect.top - 8,
                          });
                        }}
                        onMouseLeave={() => {
                          setHoveredEvent(null);
                          setTooltipPos(null);
                        }}
                        onFocus={(e) => {
                          /* keyboard navigation: show tooltip when event receives focus */
                          setHoveredEvent(event.id);
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPos({
                            x: rect.left + rect.width / 2,
                            y: rect.top - 8,
                          });
                        }}
                        onBlur={() => {
                          setHoveredEvent(null);
                          setTooltipPos(null);
                        }}
                        className="zendir-timeline-region"
                        style={{
                          position: "absolute",
                          left,
                          width,
                          minWidth: 50,
                          height: cardHeight,
                          top: Math.min(
                            baseTop + stackOffset,
                            (track.height || trackHeight) - cardHeight - 4,
                          ),
                          backgroundColor: "rgba(11, 26, 40, 0.95)",
                          border: `1px solid ${isHovered ? color : `${tokens.colors.accent.primary}30`}`,
                          borderRadius: "4px",
                          cursor: onEventClick ? "pointer" : "default",
                          display: "flex",
                          alignItems: "center",
                          overflow: "hidden",
                          zIndex: isHovered
                            ? Z.trackEventHovered
                            : Z.trackEvent,
                          boxShadow: isHovered
                            ? `0 8px 24px rgba(0, 0, 0, 0.5), 0 0 0 1px ${color}60`
                            : "0 2px 6px rgba(0, 0, 0, 0.25)",
                          transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                          transform: isHovered
                            ? "translateY(-3px) scale(1.02)"
                            : "none",
                        }}
                      >
                        {/* Team-only top stripe; status is not duplicated as a line (shape + border/hover carry severity). */}
                        {teamAccent ? (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 2,
                              backgroundColor: teamAccent.color,
                              borderRadius: "4px 4px 0 0",
                              zIndex: 1,
                              pointerEvents: "none",
                            }}
                            aria-hidden
                          />
                        ) : null}

                        {/* Content: status symbol + title */}
                        <div
                          style={{
                            flex: 1,
                            padding: `${teamAccent ? 6 : 4}px 8px 4px 8px`,
                            overflow: "hidden",
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              minWidth: 0,
                            }}
                          >
                            <TimelineStatusMarker
                              status={displayStatus}
                              fillColor={color}
                              size={hasOverlap ? 8 : 10}
                            />
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 500, // AstroUXDS medium (was 600)
                                color: "#fff",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minWidth: 0,
                              }}
                            >
                              {event.title}
                            </span>
                          </div>

                          {event.subtitle && !hasOverlap && (
                            <div
                              style={{
                                fontSize: "0.5625rem",
                                color: "rgba(255, 255, 255, 0.5)",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                marginTop: 1,
                                paddingLeft: 16,
                              }}
                            >
                              {event.subtitle}
                            </div>
                          )}
                        </div>

                        {/* Overlap indicator badge */}
                        {hasOverlap && overlap.stackIndex === 0 && (
                          <div
                            style={{
                              position: "absolute",
                              top: -6,
                              right: -6,
                              minWidth: 18,
                              height: 18,
                              backgroundColor: tokens.colors.status.caution,
                              borderRadius: 9,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.625rem",
                              fontWeight: 700,
                              color: "#000",
                              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
                              zIndex: Z.sticky - 5,
                            }}
                            title={`${overlap.overlapCount + 1} overlapping events`}
                          >
                            +{overlap.overlapCount}
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}

          {/* Current time playhead */}
          {playheadPercent !== null && (
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `calc(${trackLabelWidth}px + (100% - ${trackLabelWidth}px) * ${playheadPercent} / 100)`,
                width: 2,
                backgroundColor: tokens.colors.accent.primary,
                boxShadow: `0 0 12px ${tokens.colors.accent.primary}`,
                zIndex: Z.playhead,
                pointerEvents: "none",
                transition: "left 0.4s linear",
              }}
            >
              {/* Playhead arrow indicator */}
              <div
                style={{
                  position: "absolute",
                  top: 22,
                  left: -5,
                  width: 12,
                  height: 12,
                  backgroundColor: tokens.colors.accent.primary,
                  borderRadius: 2,
                  transform: "rotate(45deg)",
                  boxShadow: `0 0 8px ${tokens.colors.accent.primary}`,
                }}
              />

              {/* Playhead time label */}
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: tokens.colors.accent.primary,
                  color: tokens.colors.background.base,
                  fontSize: "0.5625rem",
                  fontWeight: 500, // AstroUXDS medium (was 600)
                  fontFamily: tokens.typography.fontFamily.mono,
                  padding: "2px 6px",
                  borderRadius: 3,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                }}
              >
                {formatTime(currentPlayheadTime, true)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rich Hover Tooltip */}
      {hoveredEventData &&
        tooltipPos &&
        (() => {
          const tooltipColor = getStatusColor(hoveredEventData.status);
          const teamTip = getTimelineTeamAccent(hoveredEventData);
          return (
            <div
              style={{
                position: "fixed",
                left: tooltipPos.x,
                top: tooltipPos.y,
                transform: "translate(-50%, -100%)",
                zIndex: Z.tooltip,
                pointerEvents: "none",
                animation: "zendir-tooltip-appear 150ms ease",
              }}
            >
              <div
                style={{
                  backgroundColor: "rgba(15, 23, 42, 0.98)",
                  border: `1px solid ${tooltipColor}50`,
                  borderRadius: tokens.borderRadius.md,
                  padding: "12px 16px",
                  minWidth: 220,
                  maxWidth: 320,
                  boxShadow: `0 12px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px ${tooltipColor}30`,
                  backdropFilter: "blur(12px)",
                }}
              >
                {/* Header with status — Astro UX shape */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <TimelineStatusMarker
                    status={hoveredEventData.status}
                    fillColor={tooltipColor}
                    size={10}
                  />
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 500, // AstroUXDS medium (was 600)
                      color: tooltipColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {getStatusLabel(hoveredEventData.status)}
                  </span>
                  {hoveredEventData.badge && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.625rem",
                        fontWeight: 500, // AstroUXDS medium (was 600)
                        padding: "2px 6px",
                        borderRadius: tokens.borderRadius.sm,
                        backgroundColor: `${tooltipColor}20`,
                        color: tooltipColor,
                      }}
                    >
                      {hoveredEventData.badge}
                    </span>
                  )}
                </div>

                {/* Team id + accent (or legacy color-only) */}
                {teamTip && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        backgroundColor: teamTip.color,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "0.6875rem",
                        color: teamTip.color,
                        fontWeight: 500,
                      }}
                    >
                      {getTimelineTeamDisplayLabel(hoveredEventData, teamTip)}
                    </span>
                  </div>
                )}

                {/* Title */}
                <div
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: 500, // AstroUXDS medium (was 600)
                    color: "#fff",
                    marginBottom: 4,
                  }}
                >
                  {hoveredEventData.title}
                </div>

                {/* Subtitle */}
                {hoveredEventData.subtitle && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "rgba(255, 255, 255, 0.6)",
                      marginBottom: 12,
                    }}
                  >
                    {hoveredEventData.subtitle}
                  </div>
                )}

                {/* Time details */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "6px 12px",
                    fontSize: "0.6875rem",
                    borderTop: `1px solid ${tokens.colors.border.muted}30`,
                    paddingTop: 10,
                  }}
                >
                  <span style={{ color: tokens.colors.text.tertiary }}>
                    Start:
                  </span>
                  <span
                    style={{
                      color: tokens.colors.text.primary,
                      fontFamily: tokens.typography.fontFamily.mono,
                    }}
                  >
                    {formatTime(hoveredEventData.start, true)}
                  </span>

                  {hoveredEventData.end && (
                    <>
                      <span style={{ color: tokens.colors.text.tertiary }}>
                        End:
                      </span>
                      <span
                        style={{
                          color: tokens.colors.text.primary,
                          fontFamily: tokens.typography.fontFamily.mono,
                        }}
                      >
                        {formatTime(hoveredEventData.end, true)}
                      </span>

                      <span style={{ color: tokens.colors.text.tertiary }}>
                        Duration:
                      </span>
                      <span
                        style={{
                          color: tokens.colors.accent.secondary,
                          fontWeight: 500,
                        }}
                      >
                        {formatDuration(
                          hoveredEventData.end.getTime() -
                            hoveredEventData.start.getTime(),
                        )}
                      </span>
                    </>
                  )}

                  <span style={{ color: tokens.colors.text.tertiary }}>
                    UTC:
                  </span>
                  <span
                    style={{
                      color: tokens.colors.text.secondary,
                      fontFamily: tokens.typography.fontFamily.mono,
                      fontSize: "0.625rem",
                    }}
                  >
                    {formatUTC(hoveredEventData.start)}
                  </span>
                </div>

                {/* Click hint */}
                {onEventClick && (
                  <div
                    style={{
                      marginTop: 10,
                      paddingTop: 8,
                      borderTop: `1px solid ${tokens.colors.border.muted}20`,
                      fontSize: "0.625rem",
                      color: tokens.colors.text.tertiary,
                      textAlign: "center",
                    }}
                  >
                    Click for details
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div
                style={{
                  position: "absolute",
                  bottom: -6,
                  left: "50%",
                  transform: "translateX(-50%) rotate(45deg)",
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(15, 23, 42, 0.98)",
                  borderRight: `1px solid ${tooltipColor}50`,
                  borderBottom: `1px solid ${tooltipColor}50`,
                }}
              />
            </div>
          );
        })()}

      {/* Styles */}
      <style>{`
        .zendir-timeline-region:hover {
          z-index: 100 !important;
        }
        .zendir-timeline-region:focus {
          outline: 2px solid ${tokens.colors.accent.primary};
          outline-offset: 2px;
        }
        .zendir-timeline-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .zendir-timeline-scroll::-webkit-scrollbar-track {
          background: ${tokens.colors.background.elevated};
          border-radius: 4px;
        }
        .zendir-timeline-scroll::-webkit-scrollbar-thumb {
          background: ${tokens.colors.accent.primary}40;
          border-radius: 4px;
        }
        .zendir-timeline-scroll::-webkit-scrollbar-thumb:hover {
          background: ${tokens.colors.accent.primary}60;
        }
        @keyframes zendir-tooltip-appear {
          from {
            opacity: 0;
            transform: translate(-50%, -100%) translateY(8px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -100%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
});

// ============================================================================
// Scatter View (Dot timeline - like reference image)
// ============================================================================

interface ScatterViewProps {
  events: TimelineEvent[];
  tracks: TimelineTrackDef[];
  start: Date;
  end: Date;
  trackHeight: number;
  onEventClick?: (event: TimelineEvent) => void;
  timeFormat?: TimeFormat;
  referenceTime?: Date;
  zoom?: number;
}

const ScatterView = memo(function ScatterView({
  events,
  tracks,
  start,
  end,
  trackHeight,
  onEventClick,
  timeFormat = "relative",
  referenceTime,
  zoom = 1,
}: ScatterViewProps) {
  const { tokens } = useTheme();
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const totalDuration = end.getTime() - start.getTime();
  const trackLabelWidth = 140;
  const refTime = referenceTime || start;

  // Format time based on format setting
  const formatTimeLabel = useCallback(
    (date: Date) => {
      if (timeFormat === "relative") {
        const diff = date.getTime() - refTime.getTime();
        const totalSeconds = Math.floor(Math.abs(diff) / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        }
        return `${minutes}m ${seconds}s`;
      } else if (timeFormat === "utc") {
        return date.toISOString().slice(11, 19) + " UTC";
      }
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    [timeFormat, refTime],
  );

  // Generate time labels (tied to zoom like Chart view)
  const timeLabels = useMemo(() => {
    const labels: { time: Date; percent: number }[] = [];
    const baseLabelCount = 8;
    const labelCount = Math.round(baseLabelCount * zoom);

    for (let i = 0; i <= labelCount; i++) {
      const percent = (i / labelCount) * 100;
      const time = new Date(start.getTime() + (totalDuration * percent) / 100);
      labels.push({ time, percent });
    }
    return labels;
  }, [start, totalDuration, zoom]);

  // Group events by track
  const eventsByTrack = useMemo(() => {
    const grouped: Record<string, TimelineEvent[]> = {};
    tracks.forEach((track) => {
      grouped[track.id] = events.filter((e) => e.track === track.id);
    });
    const unassigned = events.filter((e) => !e.track);
    if (unassigned.length > 0 && tracks.length > 0) {
      grouped[tracks[0].id] = [...(grouped[tracks[0].id] || []), ...unassigned];
    }
    return grouped;
  }, [events, tracks]);

  const getEventPosition = (event: TimelineEvent) => {
    const eventTime = event.start.getTime();
    return ((eventTime - start.getTime()) / totalDuration) * 100;
  };

  const getStatusColor = (status?: string) => {
    if (!status) return tokens.colors.accent.primary;
    return (
      tokens.colors.status[status as keyof typeof tokens.colors.status] ||
      tokens.colors.accent.primary
    );
  };

  const hoveredEventData = events.find((e) => e.id === hoveredEvent);

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          width: `${zoom * 100}%`,
          minWidth: "100%",
          position: "relative",
        }}
      >
        {/* Time axis */}
        <div
          style={{
            position: "relative",
            height: 32,
            marginLeft: trackLabelWidth,
            borderBottom: `1px solid ${tokens.colors.accent.primary}15`,
          }}
        >
          {timeLabels.map((label, index) => (
            <span
              key={index}
              style={{
                position: "absolute",
                left: `${label.percent}%`,
                transform: "translateX(-50%)",
                top: 8,
                fontSize: "0.6875rem",
                color: tokens.colors.text.tertiary,
                fontFamily: tokens.typography.fontFamily.mono,
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {formatTimeLabel(label.time)}
            </span>
          ))}
        </div>

        {/* Tracks */}
        {tracks.map((track, trackIndex) => (
          <div
            key={track.id}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              height: track.height || trackHeight,
              borderBottom:
                trackIndex < tracks.length - 1
                  ? `1px solid ${tokens.colors.border.muted}20`
                  : "none",
            }}
          >
            {/* Track label */}
            <div
              style={{
                width: trackLabelWidth,
                flexShrink: 0,
                padding: "0 16px",
                fontSize: "0.8125rem",
                fontWeight: 500, // AstroUXDS medium (was 600)
                color: tokens.colors.text.primary,
              }}
            >
              {track.label}
            </div>

            {/* Track line */}
            <div
              style={{
                flex: 1,
                position: "relative",
                height: "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* Horizontal guide line */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  height: 1,
                  backgroundColor: tokens.colors.border.muted,
                  opacity: 0.3,
                }}
              />

              {/* Event dots — Astro UX status shapes. Hitbox is always 24px (WCAG 2.5.5 minimum). */}
              {(eventsByTrack[track.id] || []).map((event) => {
                const leftPercent = getEventPosition(event);
                const color = getStatusColor(event.status);
                const isHovered = hoveredEvent === event.id;
                const dotSize = isHovered ? 16 : 12;
                /* Transparent padding expands the clickable hit area to ≥24×24 without growing the visible dot. */
                const hitboxPad = Math.max(0, (24 - dotSize) / 2);
                const effectiveStatus = event.status ?? "normal";
                const scatterTeamAccent = getTimelineTeamAccent(event);

                return (
                  <button
                    key={event.id}
                    type="button"
                    aria-label={`${event.title}${event.status ? `, status: ${event.status}` : ""}`}
                    onClick={() => onEventClick?.(event)}
                    onMouseEnter={(e) => {
                      setHoveredEvent(event.id);
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPosition({
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                      });
                    }}
                    onMouseLeave={() => {
                      setHoveredEvent(null);
                      setTooltipPosition(null);
                    }}
                    onFocus={(e) => {
                      /* keyboard navigation: show tooltip on focus */
                      setHoveredEvent(event.id);
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPosition({
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                      });
                    }}
                    onBlur={() => {
                      setHoveredEvent(null);
                      setTooltipPosition(null);
                    }}
                    style={{
                      position: "absolute",
                      left: `${leftPercent}%`,
                      transform: "translateX(-50%)",
                      /* visible size + transparent padding = ≥24px touch/click target */
                      width: dotSize,
                      height: dotSize,
                      padding: hitboxPad,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      transition: "all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                      zIndex: isHovered ? Z.trackEventHovered : Z.trackEvent,
                      lineHeight: 0,
                      borderRadius: "50%",
                      boxSizing: "content-box",
                      boxShadow: scatterTeamAccent
                        ? `0 0 0 2px ${scatterTeamAccent.color}`
                        : undefined,
                    }}
                  >
                    <TimelineStatusMarker
                      status={effectiveStatus}
                      fillColor={color}
                      size={dotSize}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Floating tooltip */}
      {hoveredEventData &&
        tooltipPosition &&
        (() => {
          const scatterTipColor = getStatusColor(hoveredEventData.status);
          const scatterTeamTip = getTimelineTeamAccent(hoveredEventData);
          return (
            <div
              className="zendir-scatter-tooltip"
              style={{
                position: "fixed",
                left: tooltipPosition.x,
                top: tooltipPosition.y - 12,
                transform: "translate(-50%, -100%)",
                backgroundColor: "rgba(11, 26, 40, 0.98)",
                border: `1px solid ${scatterTipColor}40`,
                borderRadius: tokens.borderRadius.lg,
                padding: "12px 16px",
                minWidth: 200,
                maxWidth: 320,
                zIndex: Z.tooltip,
                boxShadow: `0 12px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px ${scatterTipColor}20`,
                backdropFilter: "blur(12px)",
                animation:
                  "zendir-tooltip-in 150ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              {/* Arrow */}
              <div
                style={{
                  position: "absolute",
                  bottom: -6,
                  left: "50%",
                  transform: "translateX(-50%) rotate(45deg)",
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(11, 26, 40, 0.98)",
                  borderRight: `1px solid ${scatterTipColor}40`,
                  borderBottom: `1px solid ${scatterTipColor}40`,
                }}
              />

              {/* Status header — Astro UX shape */}
              {hoveredEventData.status && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <TimelineStatusMarker
                    status={hoveredEventData.status}
                    fillColor={scatterTipColor}
                    size={10}
                  />
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: scatterTipColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {hoveredEventData.status}
                  </span>
                  {hoveredEventData.badge && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.625rem",
                        fontWeight: 500,
                        padding: "2px 6px",
                        borderRadius: tokens.borderRadius.sm,
                        backgroundColor: `${scatterTipColor}20`,
                        color: scatterTipColor,
                      }}
                    >
                      {hoveredEventData.badge}
                    </span>
                  )}
                </div>
              )}

              {/* Team id + accent (or legacy color-only) */}
              {scatterTeamTip && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      backgroundColor: scatterTeamTip.color,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.6875rem",
                      color: scatterTeamTip.color,
                      fontWeight: 500,
                    }}
                  >
                    {getTimelineTeamDisplayLabel(
                      hoveredEventData,
                      scatterTeamTip,
                    )}
                  </span>
                </div>
              )}

              {/* Title */}
              <div
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#fff",
                  marginBottom: 8,
                }}
              >
                {hoveredEventData.title}
              </div>

              {/* Details */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.75rem",
                  }}
                >
                  <span style={{ color: tokens.colors.text.tertiary }}>
                    Time:
                  </span>
                  <span
                    style={{
                      color: tokens.colors.text.secondary,
                      fontFamily: tokens.typography.fontFamily.mono,
                    }}
                  >
                    {formatTimeLabel(hoveredEventData.start)}
                  </span>
                </div>

                {hoveredEventData.badge && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.75rem",
                    }}
                  >
                    <span style={{ color: tokens.colors.text.tertiary }}>
                      Trigger:
                    </span>
                    <span
                      style={{
                        color: safeAccentText(tokens.colors.accent.primary),
                        fontWeight: 500,
                      }}
                    >
                      {hoveredEventData.badge}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.75rem",
                  }}
                >
                  <span style={{ color: tokens.colors.text.tertiary }}>
                    UTC:
                  </span>
                  <span
                    style={{
                      color: tokens.colors.text.secondary,
                      fontFamily: tokens.typography.fontFamily.mono,
                    }}
                  >
                    {hoveredEventData.start
                      .toISOString()
                      .replace("T", " ")
                      .slice(0, 19)}
                  </span>
                </div>

                {hoveredEventData.subtitle && (
                  <div
                    style={{
                      marginTop: 4,
                      paddingTop: 8,
                      borderTop: `1px solid ${tokens.colors.border.muted}`,
                      fontSize: "0.75rem",
                      color: tokens.colors.text.secondary,
                    }}
                  >
                    {hoveredEventData.subtitle}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

      {/* Styles */}
      <style>{`
        @keyframes zendir-tooltip-in {
          from {
            opacity: 0;
            transform: translate(-50%, -100%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -100%) scale(1);
          }
        }
        .zendir-scatter-tooltip::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(135deg, ${tokens.colors.accent.primary}1a 0%, transparent 50%);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
});

// ============================================================================
// Advanced filters toolbar (search, tracks, status, duration, teams)
// ============================================================================

interface TimelineTeamFilterOption {
  key: string;
  label: string;
  /** Accent swatch; empty when bucket is "no team" */
  color: string;
  isNoneBucket: boolean;
}

function buildTeamFilterOptions(
  events: TimelineEvent[],
  tokens: ThemeTokens,
  categoryLabel: string = "team",
): TimelineTeamFilterOption[] {
  const map = new Map<string, TimelineTeamFilterOption>();
  for (const event of events) {
    const key = getTimelineTeamFilterKey(event);
    if (map.has(key)) continue;
    const accent = getTimelineTeamAccent(event);
    const isNone = key === TIMELINE_FILTER_TEAM_NONE;
    const isLegacy = key === TIMELINE_FILTER_TEAM_LEGACY;
    const label = isNone
      ? `No ${categoryLabel.toLowerCase()}`
      : isLegacy
        ? "Accent only"
        : accent
          ? getTimelineTeamDisplayLabel(event, accent)
          : key;
    const color = isNone ? "" : (accent?.color ?? tokens.colors.text.tertiary);
    map.set(key, { key, label, color, isNoneBucket: isNone });
  }
  const list = [...map.values()];
  list.sort((a, b) => {
    if (a.isNoneBucket !== b.isNoneBucket) return a.isNoneBucket ? 1 : -1;
    if (a.key === TIMELINE_FILTER_TEAM_LEGACY)
      return b.key === TIMELINE_FILTER_TEAM_NONE ? -1 : 1;
    if (b.key === TIMELINE_FILTER_TEAM_LEGACY)
      return a.key === TIMELINE_FILTER_TEAM_NONE ? 1 : -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });
  return list;
}

interface TimelineFiltersToolbarProps {
  filter: TimelineFilter;
  onFilterChange: (next: TimelineFilter) => void;
  trackOptions: { id: string; label: string }[];
  events: TimelineEvent[];
  expanded: boolean;
  /** Display label for the team/secondary-category section (defaults to "Teams"). */
  teamLabel: string;
}

/** Expandable filter panel — rendered below the header when expanded. Search + toggle live in the header. */
const TimelineFiltersPanel = memo(function TimelineFiltersPanel({
  filter,
  onFilterChange,
  trackOptions,
  events,
  expanded,
  teamLabel,
}: TimelineFiltersToolbarProps) {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const accentColor = tokens.colors.accent.primary;

  const teamOptions = useMemo(
    () => buildTeamFilterOptions(events, tokens, teamLabel),
    [events, tokens, teamLabel],
  );

  const patch = useCallback(
    (partial: Partial<TimelineFilter>) => {
      onFilterChange(normalizeTimelineFilter({ ...filter, ...partial }));
    },
    [filter, onFilterChange],
  );

  const clearAll = useCallback(() => {
    onFilterChange({});
  }, [onFilterChange]);

  const toggleTrack = useCallback(
    (id: string) => {
      const cur = filter.tracks ?? [];
      const next = cur.includes(id)
        ? cur.filter((t) => t !== id)
        : [...cur, id];
      patch({ tracks: next.length ? next : undefined });
    },
    [filter.tracks, patch],
  );

  const toggleStatus = useCallback(
    (st: TimelineEvent["status"]) => {
      const cur = filter.status ?? [];
      const next = cur.includes(st)
        ? cur.filter((s) => s !== st)
        : [...cur, st];
      patch({ status: next.length ? next : undefined });
    },
    [filter.status, patch],
  );

  const toggleTeam = useCallback(
    (teamKey: string) => {
      const cur = filter.teams ?? [];
      const next = cur.includes(teamKey)
        ? cur.filter((k) => k !== teamKey)
        : [...cur, teamKey];
      patch({ teams: next.length ? next : undefined });
    },
    [filter.teams, patch],
  );

  /* Astro-compliant pill: borderRadius.md, 2px 8px, uppercase xxs (10px), 500 weight, 0.06em tracking.
     Active pill uses accent border + faint accent bg (mirrors LogViewer severity filter pattern). */
  const pillStyle = useCallback(
    (active: boolean, tintColor?: string): React.CSSProperties => {
      const c = tintColor || accentColor;
      return {
        padding: "2px 8px",
        borderRadius: tokens.borderRadius.md,
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        lineHeight: 1.4,
        border: `1px solid ${active ? `${c}66` : tokens.colors.border.muted}`,
        backgroundColor: active ? `${c}22` : "transparent",
        color: active ? c : tokens.colors.text.secondary,
        cursor: "pointer",
        transition: tokens.animation.fast,
      };
    },
    [accentColor, tokens],
  );

  /* Section header: Astro DataTable column-header pattern — bold, uppercase, 10px, 0.06em tracking. */
  const sectionHeader = (text: string): React.ReactElement => (
    <div
      style={{
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.colors.text.tertiary,
        marginBottom: 8,
      }}
    >
      {text}
    </div>
  );

  if (!expanded) return null;

  return (
    <div
      id="zendir-timeline-filters-panel"
      style={{
        padding: `${tokens.spacing.smd} ${tokens.spacing.md}`,
        borderBottom: `1px solid ${tokens.colors.border.muted}`,
        backgroundColor: isTransparentTheme
          ? "transparent"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)" as const,
        }),
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacing.smd,
      }}
    >
      {/* Tracks */}
      {trackOptions.length > 0 ? (
        <div role="group" aria-label="Filter by track">
          {sectionHeader("Tracks")}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: tokens.spacing.xs,
            }}
          >
            {trackOptions.map((t) => {
              const active = filter.tracks?.includes(t.id) ?? false;
              return (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleTrack(t.id)}
                  style={pillStyle(active)}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Status — pills tinted with their status color */}
      <div role="group" aria-label="Filter by status">
        {sectionHeader("Status")}
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: tokens.spacing.xs }}
        >
          {ALL_TIMELINE_STATUSES.map((st) => {
            const active = filter.status?.includes(st) ?? false;
            const statusColor = st ? tokens.colors.status[st] : accentColor;
            return (
              <button
                key={st}
                type="button"
                aria-pressed={active}
                onClick={() => toggleStatus(st)}
                style={{
                  ...pillStyle(active, statusColor),
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: active ? statusColor : `${statusColor}50`,
                    flexShrink: 0,
                    transition: tokens.animation.fast,
                  }}
                />
                {st}
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration + Teams on one row where space allows */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: `${tokens.spacing.smd} ${tokens.spacing.lg}`,
          alignItems: "flex-start",
        }}
      >
        <div
          role="group"
          aria-label="Filter by duration"
          style={{ minWidth: 0 }}
        >
          {sectionHeader("Duration")}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: tokens.spacing.xs,
            }}
          >
            {(["all", "point", "range"] as const).map((key) => {
              const active = (filter.eventShape ?? "all") === key;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => patch({ eventShape: key })}
                  style={pillStyle(active)}
                >
                  {key === "all"
                    ? "All"
                    : key === "point"
                      ? "Instant"
                      : "Range"}
                </button>
              );
            })}
          </div>
        </div>

        {teamOptions.length > 0 ? (
          <div
            role="group"
            aria-label={`Filter by ${teamLabel.toLowerCase()}`}
            style={{ flex: 1, minWidth: 180 }}
          >
            {sectionHeader(teamLabel)}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: tokens.spacing.xs,
              }}
            >
              {teamOptions.map((opt) => {
                const active = filter.teams?.includes(opt.key) ?? false;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleTeam(opt.key)}
                    style={{
                      ...pillStyle(active, opt.color || undefined),
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      maxWidth: "100%",
                    }}
                  >
                    {opt.isNoneBucket ? (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          border: `${tokens.borders?.width?.thick ?? "2px"} solid ${tokens.colors.border.muted}`,
                          flexShrink: 0,
                          boxSizing: "border-box",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: opt.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* Clear all — only when filters active. Uses SDK Button for consistent focus ring & a11y. */}
      {isTimelineFilterActive(filter) ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="borderless" size="small" onClick={clearAll}>
            Clear all
          </Button>
        </div>
      ) : null}
    </div>
  );
});

// ============================================================================
// Main Component
// ============================================================================

export const UnifiedTimeline = memo(function UnifiedTimeline({
  title = "Timeline",
  events,
  tracks = [],
  start: propStart,
  end: propEnd,
  defaultView = "chart",
  viewMode: controlledViewMode,
  onViewModeChange,
  onEventClick,
  showViewToggle = true,
  maxHeight,
  trackHeight = 56,
  className = "",
  timeFormat = "absolute",
  zoomable = false,
  initialZoom = 1,
  showFilters: _showFilters = true,
  filtersDefaultExpanded = false,
  hideEmptyTracksWhenFiltered = true,
  filter: _filter,
  onFilterChange: _onFilterChange,
  showCount: _showCount = true,
  loading = false,
  referenceTime,
  showPlayhead = true,
  playheadTime,
  onPlayheadChange: _onPlayheadChangeTimeline,
  showDayMarkers = true,
  teamLabel: teamLabelProp,
}: UnifiedTimelineProps) {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const { isMobile } = useBreakpoint();
  const categoryCtx = useCategoryPalette();
  /** Explicit prop → CategoryProvider label → default "Teams" */
  const teamLabel = teamLabelProp ?? categoryCtx?.categoryLabel ?? "Teams";
  const [internalViewMode, setInternalViewMode] =
    useState<TimelineViewMode>(defaultView);
  const [zoom, setZoom] = useState(initialZoom);
  const [_internalFilter, _setInternalFilter] = useState<TimelineFilter>({});
  const [filtersExpanded, setFiltersExpanded] = useState(
    filtersDefaultExpanded,
  );
  const filter = _filter !== undefined ? _filter : _internalFilter;
  const filterChipCount = _showFilters ? countTimelineFilterChips(filter) : 0;

  const setFilter = useCallback(
    (next: TimelineFilter) => {
      const n = normalizeTimelineFilter(next);
      if (_filter === undefined) _setInternalFilter(n);
      _onFilterChange?.(n);
    },
    [_filter, _onFilterChange],
  );

  const patchFilter = useCallback(
    (partial: Partial<TimelineFilter>) => {
      setFilter(normalizeTimelineFilter({ ...filter, ...partial }));
    },
    [filter, setFilter],
  );

  const viewMode = controlledViewMode ?? internalViewMode;

  const handleViewModeChange = useCallback(
    (mode: TimelineViewMode) => {
      setInternalViewMode(mode);
      onViewModeChange?.(mode);
    },
    [onViewModeChange],
  );

  // Auto-calculate time range if not provided
  const { start, end } = useMemo(() => {
    if (propStart && propEnd) {
      return { start: propStart, end: propEnd };
    }

    if (events.length === 0) {
      const now = new Date();
      return {
        start: new Date(now.getTime() - 3600000),
        end: new Date(now.getTime() + 3600000),
      };
    }

    let minTime = events[0].start.getTime();
    let maxTime = events[0].start.getTime();

    events.forEach((event) => {
      minTime = Math.min(minTime, event.start.getTime());
      maxTime = Math.max(maxTime, (event.end || event.start).getTime());
    });

    // Add 10% padding
    const duration = maxTime - minTime;
    const padding = duration * 0.1;

    return {
      start: new Date(minTime - padding),
      end: new Date(maxTime + padding),
    };
  }, [events, propStart, propEnd]);

  // Auto-generate tracks from events if not provided
  const effectiveTracks = useMemo(() => {
    if (tracks.length > 0) return tracks;

    const trackSet = new Set<string>();
    events.forEach((event) => {
      if (event.track) trackSet.add(event.track);
    });

    if (trackSet.size === 0) {
      return [{ id: "default", label: "Events", height: trackHeight }];
    }

    return Array.from(trackSet).map((id) => ({
      id,
      label: id,
      height: trackHeight,
    }));
  }, [events, tracks, trackHeight]);

  const filteredEvents = useMemo(
    () => events.filter((e) => matchesTimelineFilter(e, filter)),
    [events, filter],
  );

  const effectiveTracksForViews = useMemo(() => {
    if (!hideEmptyTracksWhenFiltered || !isTimelineFilterActive(filter))
      return effectiveTracks;
    const ids = new Set(filteredEvents.map((e) => e.track || "default"));
    return effectiveTracks.filter((t) => ids.has(t.id));
  }, [effectiveTracks, filteredEvents, filter, hideEmptyTracksWhenFiltered]);

  const trackOptionsForToolbar = useMemo(() => {
    if (tracks.length > 0)
      return tracks.map((t) => ({ id: t.id, label: t.label }));
    const u = new Set<string>();
    events.forEach((e) => {
      if (e.track) u.add(e.track);
    });
    if (u.size === 0) return [{ id: "default", label: "Events" }];
    return [...u].sort().map((id) => ({ id, label: id }));
  }, [events, tracks]);

  // Sort filtered events by start time (newest first for list view)
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort(
      (a, b) => b.start.getTime() - a.start.getTime(),
    );
  }, [filteredEvents]);

  const countLabel = !_showCount
    ? title
    : `${title} (${filteredEvents.length}${filteredEvents.length !== events.length ? ` / ${events.length}` : ""})`;

  return (
    <div
      className={classNames("zendir-unified-timeline", className)}
      style={{
        backgroundColor: isTransparentTheme
          ? "transparent"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)" as const,
        }),
        ...(tokens.colors.border.cardStyle ?? {
          border: `1px solid ${tokens.colors.border.muted}`,
        }),
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
      }}
    >
      {/* Header — stacks vertically on mobile so controls don't overflow */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          padding: `${tokens.spacing.smd} ${tokens.spacing.md}`,
          gap: isMobile ? tokens.spacing.xs : tokens.spacing.sm,
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "1rem",
              fontWeight: 500, // AstroUXDS medium
              color: tokens.colors.text.primary,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {countLabel}
          </h3>

          {/* Spacer pushes controls right (only in row/desktop layout) */}
          {!isMobile && <div style={{ flex: 1 }} />}
        </div>
        {/* Second row on mobile, same row on desktop */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
            flex: isMobile ? undefined : 1,
            justifyContent: isMobile ? "space-between" : "flex-end",
          }}
        >
          {/* Inline search + filter icon — left of zoom/view toggle */}
          {_showFilters && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.spacing.xs,
                flex: isMobile ? 1 : undefined,
                flexShrink: 0,
              }}
            >
              {/* Compact search: expands on mobile to fill available width */}
              <div
                style={{ position: "relative", width: isMobile ? "100%" : 180 }}
              >
                <Icon
                  name="search"
                  size={14}
                  style={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: tokens.colors.text.tertiary,
                    pointerEvents: "none",
                  }}
                />
                <input
                  type="search"
                  value={filter.search ?? ""}
                  onChange={(e) =>
                    patchFilter({ search: e.target.value || undefined })
                  }
                  placeholder="Search…"
                  aria-label="Search timeline events"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    height: tokens.elementSize.sm,
                    paddingLeft: 28,
                    paddingRight: 8,
                    borderRadius: tokens.borderRadius.md,
                    border: `1px solid ${tokens.colors.border.muted}`,
                    backgroundColor: tokens.colors.background.base,
                    color: tokens.colors.text.primary,
                    fontSize: tokens.typography.fontSize.sm,
                    fontFamily: "inherit",
                    outline: "none",
                    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = tokens.colors.accent.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${tokens.colors.accent.primary}50, 0 0 20px ${tokens.colors.accent.primary}15`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = tokens.colors.border.muted;
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Filter panel toggle icon */}
              <Tooltip
                content={filtersExpanded ? "Hide filters" : "Show filters"}
              >
                <button
                  type="button"
                  aria-label={filtersExpanded ? "Hide filters" : "Show filters"}
                  aria-expanded={filtersExpanded}
                  aria-controls="zendir-timeline-filters-panel"
                  onClick={() => setFiltersExpanded((v) => !v)}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    border: `1px solid ${filterChipCount > 0 ? `${tokens.colors.accent.primary}80` : tokens.colors.border.muted}`,
                    borderRadius: tokens.borderRadius.sm,
                    backgroundColor:
                      filterChipCount > 0
                        ? `${tokens.colors.accent.primary}12`
                        : "transparent",
                    color:
                      filterChipCount > 0
                        ? tokens.colors.accent.primary
                        : tokens.colors.text.secondary,
                    cursor: "pointer",
                    transition: tokens.animation.fast,
                  }}
                >
                  <Icon name="filter" size={16} />
                  {filterChipCount > 0 ? (
                    <span
                      style={{
                        position: "absolute",
                        top: -4,
                        right: -4,
                        minWidth: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: tokens.colors.accent.primary,
                        color: "#fff",
                        fontSize: "0.5625rem",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                      }}
                    >
                      {filterChipCount}
                    </span>
                  ) : null}
                </button>
              </Tooltip>
            </div>
          )}
          {/* Right side controls */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexShrink: 0,
            }}
          >
            {/* Zoom controls - chart and scatter share zoom; list has no time axis */}
            {zoomable && (viewMode === "chart" || viewMode === "scatter") && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  paddingRight: 12,
                  borderRight: `1px solid ${tokens.colors.border.muted}`,
                }}
              >
                <Tooltip content="Zoom out">
                  <button
                    type="button"
                    aria-label="Zoom out"
                    onClick={() => setZoom(Math.max(zoom - 0.5, 1))}
                    disabled={zoom <= 1}
                    style={{
                      width: 28,
                      height: 28,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        zoom <= 1
                          ? "transparent"
                          : tokens.colors.background.elevated,
                      border: `1px solid ${tokens.colors.border.muted}`,
                      borderRadius: tokens.borderRadius.sm,
                      color:
                        zoom <= 1
                          ? tokens.colors.text.tertiary
                          : tokens.colors.text.primary,
                      cursor: zoom <= 1 ? "not-allowed" : "pointer",
                      fontSize: "1rem",
                      fontWeight: 500, // AstroUXDS medium (was 600)
                      opacity: zoom <= 1 ? 0.5 : 1,
                    }}
                  >
                    −
                  </button>
                </Tooltip>
                <span
                  style={{
                    minWidth: 40,
                    textAlign: "center",
                    fontSize: "0.75rem",
                    fontFamily: tokens.typography.fontFamily.mono,
                    color: tokens.colors.text.secondary,
                  }}
                >
                  {Math.round(zoom * 100)}%
                </span>
                <Tooltip content="Zoom in">
                  <button
                    type="button"
                    aria-label="Zoom in"
                    onClick={() => setZoom(Math.min(zoom + 0.5, 4))}
                    disabled={zoom >= 4}
                    style={{
                      width: 28,
                      height: 28,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        zoom >= 4
                          ? "transparent"
                          : tokens.colors.background.elevated,
                      border: `1px solid ${tokens.colors.border.muted}`,
                      borderRadius: tokens.borderRadius.sm,
                      color:
                        zoom >= 4
                          ? tokens.colors.text.tertiary
                          : tokens.colors.text.primary,
                      cursor: zoom >= 4 ? "not-allowed" : "pointer",
                      fontSize: "1rem",
                      fontWeight: 500, // AstroUXDS medium (was 600)
                      opacity: zoom >= 4 ? 0.5 : 1,
                    }}
                  >
                    +
                  </button>
                </Tooltip>
                <Tooltip content="Reset zoom">
                  <button
                    type="button"
                    aria-label="Reset zoom"
                    onClick={() => setZoom(1)}
                    style={{
                      height: 28,
                      padding: "0 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: tokens.colors.background.elevated,
                      border: `1px solid ${tokens.colors.border.muted}`,
                      borderRadius: tokens.borderRadius.sm,
                      color: tokens.colors.text.secondary,
                      cursor: "pointer",
                      fontSize: "0.6875rem",
                      fontWeight: 500,
                    }}
                  >
                    FIT
                  </button>
                </Tooltip>
              </div>
            )}

            {/* View toggle — role=group tells assistive tech these buttons share a purpose */}
            {showViewToggle && (
              <div
                role="group"
                aria-label="View mode"
                style={{
                  display: "flex",
                  gap: "2px",
                  backgroundColor: isTransparentTheme
                    ? "transparent"
                    : tokens.colors.background.elevated,
                  ...(isTransparentTheme && {
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)" as const,
                  }),
                  padding: "4px",
                  borderRadius: tokens.borderRadius.lg,
                }}
              >
                {(["chart", "list", "scatter"] as TimelineViewMode[]).map(
                  (mode) => {
                    const isActive = viewMode === mode;
                    const icons: Record<
                      TimelineViewMode,
                      "list" | "timeline" | "chart"
                    > = {
                      chart: "chart",
                      list: "list",
                      scatter: "timeline",
                    };
                    const labels: Record<TimelineViewMode, string> = {
                      chart: "Gantt Chart",
                      list: "List View",
                      scatter: "Scatter View",
                    };

                    return (
                      <Tooltip key={mode} content={labels[mode]}>
                        <button
                          type="button"
                          aria-label={labels[mode]}
                          aria-pressed={isActive}
                          onClick={() => handleViewModeChange(mode)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                            backgroundColor: isActive
                              ? tokens.colors.accent.primary
                              : "transparent",
                            border: "none",
                            borderRadius: tokens.borderRadius.md,
                            color: isActive
                              ? "#ffffff"
                              : tokens.colors.text.secondary,
                            cursor: "pointer",
                            transition:
                              "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: isActive ? "scale(1)" : "scale(0.95)",
                            boxShadow: isActive
                              ? `0 2px 8px ${tokens.colors.accent.primary}40`
                              : "none",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = `${tokens.colors.accent.primary}20`;
                              e.currentTarget.style.transform = "scale(1)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.transform = "scale(0.95)";
                            }
                          }}
                        >
                          <Icon name={icons[mode]} size={16} />
                        </button>
                      </Tooltip>
                    );
                  },
                )}
              </div>
            )}
          </div>{" "}
          {/* end right side controls */}
        </div>{" "}
        {/* end second-row */}
      </div>{" "}
      {/* end header */}
      {_showFilters ? (
        <TimelineFiltersPanel
          filter={filter}
          onFilterChange={setFilter}
          trackOptions={trackOptionsForToolbar}
          events={events}
          expanded={filtersExpanded}
          teamLabel={teamLabel}
        />
      ) : null}
      {/* Loading State */}
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${tokens.colors.background.overlay}80`,
            backdropFilter: "blur(4px)",
            zIndex: Z.loadingOverlay,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${tokens.colors.border.muted}`,
              borderTopColor: tokens.colors.accent.primary,
              borderRadius: "50%",
              animation: "zendir-spin 1s linear infinite",
            }}
          />
        </div>
      )}
      {/* Content */}
      <div
        style={{
          padding: viewMode === "list" ? "16px 20px" : "16px",
          maxHeight: maxHeight,
          overflowY: maxHeight ? "auto" : undefined,
          overflowX: viewMode !== "list" ? "auto" : undefined,
        }}
      >
        {events.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 0",
              color: tokens.colors.text.tertiary,
              gap: "12px",
            }}
          >
            <Icon name="clock" size={32} />
            <span style={{ fontSize: "0.875rem" }}>No events to display</span>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 24px",
              color: tokens.colors.text.tertiary,
              gap: 12,
              textAlign: "center",
            }}
          >
            <Icon name="search" size={32} />
            <span
              style={{
                fontSize: "0.875rem",
                color: tokens.colors.text.secondary,
              }}
            >
              No events match the current filters.
            </span>
            {_showFilters && isTimelineFilterActive(filter) ? (
              <button
                type="button"
                onClick={() => setFilter({})}
                style={{
                  marginTop: 4,
                  padding: "8px 16px",
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${tokens.colors.accent.primary}`,
                  backgroundColor: `${tokens.colors.accent.primary}18`,
                  color: tokens.colors.accent.primary,
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                }}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : viewMode === "list" ? (
          // List View
          sortedEvents.map((event, index) => (
            <EventListItem
              key={event.id}
              event={event}
              isLast={index === sortedEvents.length - 1}
              onEventClick={onEventClick}
            />
          ))
        ) : viewMode === "scatter" ? (
          // Scatter View (Dots on timeline) - uses same zoom as chart
          <ScatterView
            events={filteredEvents}
            tracks={effectiveTracksForViews}
            start={start}
            end={end}
            trackHeight={trackHeight}
            onEventClick={onEventClick}
            timeFormat={timeFormat}
            referenceTime={referenceTime || start}
            zoom={zoom}
          />
        ) : (
          // Chart View (Gantt)
          <ChartView
            events={filteredEvents}
            tracks={effectiveTracksForViews}
            start={start}
            end={end}
            trackHeight={trackHeight}
            onEventClick={onEventClick}
            zoom={zoom}
            showPlayhead={showPlayhead}
            playheadTime={playheadTime}
            onPlayheadChange={_onPlayheadChangeTimeline}
            showDayMarkers={showDayMarkers}
            timeFormat={timeFormat}
          />
        )}
      </div>
      {/* Global Styles */}
      <style>{`
        @keyframes zendir-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

export default UnifiedTimeline;
